// Shared domain types. Framework-agnostic.

export type SensorData = Record<string, string | number | null>;

export type StatusLevel =
  | 'running'
  | 'ready'
  | 'idle'
  | 'error'
  | 'state_off'
  | 'offline'
  | 'off';

export interface Reading {
  machine_id:   string;
  company_slug: string;
  subject:      string;
  ts:           number;
  data:         SensorData;
}

export interface Envelope {
  machine_id:   string;
  company_slug: string;
  subject:      string;
  ts:           number;
  data:         Record<string, unknown>;
}

export interface MachineState {
  key:      string;
  latest:   SensorData;
  ts:       number;
  count:    number;
  level:    StatusLevel;
  history:  Reading[];
  /** Wall-clock ms of the last ingestBatch that touched this machine.
   *  Distinct from `ts` (device-timestamp of the most recent reading).
   *  When the consumer is replaying historical messages, `lastArrivalWallMs`
   *  advances near real-time while `ts` trails behind — the gap signals
   *  the card is "fast-forwarding" through buffered data. */
  lastArrivalWallMs: number;
}

export interface AlertStage {
  level:           string;
  operator:        '>' | '<' | '>=' | '<=' | '=' | '!=';
  threshold:       number;
  cooldown_ms?:    number;
  notify_target?:  string;
  notify_type?:    string;
  webhook_url?:    string;
  webhook_headers?: Record<string, string>;
}

export interface AlertRule {
  id:              string;
  machine_id:      string;
  field_name:      string;
  operator:        '>' | '<' | '>=' | '<=' | '=' | '!=';
  threshold:       number;
  notify_type:     string;
  notify_target:   string;
  enabled:         boolean;
  cooldown_ms:     number;
  sustain_ms:      number;
  stages:          AlertStage[] | null;
  webhook_url:     string | null;
  webhook_headers: Record<string, string> | null;
  webhook_body_template?: unknown;
  scope?:          'machine' | 'company';
  company_id?:     string | null;
  company_slug?:   string;
  subject_prefix?: string;
}

export interface ApiMachine {
  id:           string;
  machine_id:   string;
  /** Optional human-friendly machine name (DB column machines.name).
   *  When present this is what the dashboard surfaces as the machine label;
   *  machine_id is reserved as the disambiguating slug shown in tooltips. */
  name:         string | null;
  company_slug: string;
  site_slug:    string;
  site_name:    string;
  company_name: string;
  enrolled_at:  string | null;
  /** Wallclock of last NATS heartbeat from the bridge. ISO string or
   *  null when never connected. Used by the dashboard's first-paint
   *  fallback path to render "Last seen 12m ago" before any live frame
   *  arrives. Source: api stamps machines.last_seen on heartbeat. */
  last_seen?:   string | null;
  active:       boolean;
}

export type DashboardRole = 'viewer' | 'operator' | 'admin';

export interface UserPermissions {
  id:                string;
  user_id:           string;
  email:             string;
  display_name:      string;
  role:              DashboardRole;
  company_ids:       string[];
  site_ids:          string[];
  can_create_alerts: boolean;
  can_ack_alerts:    boolean;
  can_manage_sensors?: boolean;
}

export type Page = 'dashboard' | 'history' | 'logs' | 'alerts' | 'nats-diag';
export type ConnStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

export interface AuthUser {
  token:        string;
  /** Needed for Zitadel logout `id_token_hint` to actually end the session. */
  idToken:      string;
  name:         string;
  email:        string;
  sub:          string;
  isAdmin:      boolean;
  /** Project role keys carried by the JWT. Used by featureFlags.ts to
   *  gate WIP features behind specific roles (admins see things ops
   *  users don't, etc.) without re-reading the token elsewhere. */
  roles:        string[];
  companySlug:  string | null;
}

export type DashboardPreset = 1 | 2 | 4;

/** Two pinning modes:
 *  - 'linked': one global sensor list applied to every selected machine
 *    (apples-to-apples cross-fleet view).
 *  - 'independent': each machine has its own sensor list (drilldown view).
 *  State is kept separately for both modes so toggling never loses choices.
 */
export type ViewMode = 'linked' | 'independent';

export interface DashboardConfig {
  /** Selected machine keys (max = preset). Order = display order. */
  selectedMachines: string[];
  /** Layout preset — 1 / 2 / 4 cards side-by-side on desktop. */
  preset:           DashboardPreset;
  /** Active pin mode. */
  viewMode:         ViewMode;
  /** Linked-mode pin list (shared across all cards). */
  linkedPins:       string[];
  /** Independent-mode per-machine pin lists. */
  independentPins:  Record<string, string[]>;
}

export interface HistoryConfig {
  selectedMachines: string[];
  viewMode:         ViewMode;
  linkedPins:       string[];
  independentPins:  Record<string, string[]>;
  layout:           'stacked' | 'overlaid';
  range:            TimeRange;
  customRange:      { fromMs: number; toMs: number } | null;
}

// Quick-preset ranges shown in the range dropdown are live/1h/8h/24h; 6h/72h/7d
// stay in the union for back-compat with persisted state (migrated forward) and
// so pinFor stays total. Longer-than-24h windows are reached via the custom
// picker (capped at MAX_HISTORY_WINDOW_MS).
export type TimeRange = 'live' | '1h' | '6h' | '8h' | '24h' | '72h' | '7d' | 'custom';

/** Cap on any interactive history window — the lake serves 1-7 day
 *  slices (issue #15); longer horizons belong to PdM reports. */
export const MAX_HISTORY_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

/** Server-side aggregation bucket for zoom-progressive chart fetches. */
export type Bucket = 'raw' | '1s' | '10s' | '1m' | '1h';

/** User-defined threshold flag on a sensor reading. */
export type SetpointOp = '>' | '<' | '>=' | '<=' | '=' | '!=';

/** Alert event surfaced to the bell + toast system. Mirrors api `alert_events` row. */
export interface AlertEvent {
  id:            string;
  rule_id:       string;
  /** Optional rule name (joined server-side; fall back to rule_id). */
  rule_name?:    string;
  company_slug:  string;
  machine_id:    string;
  field:         string;
  value:         number | null;
  threshold:     number | null;
  operator:      string | null;
  stage_level:   string | null;
  /** ISO 8601. */
  fired_at:      string;
  /** Set when condition transitioned back to false (auto-cleared). */
  resolved_at:   string | null;
  /** Set when user acknowledged. */
  acked_at:      string | null;
  acked_by:      string | null;
  ack_note:      string | null;
}

export type AlertSeverity = 'fault' | 'warning' | 'info' | 'cleared';

export interface Setpoint {
  id:        string;
  /** The sensor field this watches (e.g. "KuehlerTemp"). */
  sensor:    string;
  op:        SetpointOp;
  value:     number;
  /** Optional human label. Defaults to "{sensor} {op} {value}". */
  label?:    string;
  /** Color for the chart line + cell tint. Default = warning amber. */
  color?:    string;
  /** Scope: applies to every machine, or only to specific keys. */
  scope:     'global' | 'machines';
  /** Required when scope==='machines'. */
  machineKeys?: string[];
  /** When false the setpoint exists but doesn't render. */
  enabled:   boolean;
}
